import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, SelectItemGroup } from 'primeng/api';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { lastValueFrom, map } from 'rxjs';
import {
  Campaign,
  CampaignControllerService,
  CampaignGift,
  FileControllerService,
  SubscriptionControllerService,
  SubscriptionType
} from 'src/app/admin-api';
import {
  getAllSubscriptions,
  getSubscriptionName,
  SubscriptionEnum
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-campaign-rules',
  templateUrl: './campaign-rules.component.html',
  styleUrl: './campaign-rules.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [MessageService, DatePipe]
})
export class CampaignRulesComponent implements OnInit {
  private campaignService = inject(CampaignControllerService);
  private subscriptionService = inject(SubscriptionControllerService);
  private fileService = inject(FileControllerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);

  @ViewChild('fileUpload') fileUpload: FileUpload;

  form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    description: new FormControl<string>(''),
    startDate: new FormControl<Date>(null, Validators.required),
    endDate: new FormControl<Date>(null, Validators.required),
    coverUrl: new FormControl<string>(''),
    ruleType: new FormControl<string>('MGM', Validators.required),
    status: new FormControl<string>('DRAFT', Validators.required)
  });

  giftForm = new FormGroup({
    ean: new FormControl<string>(''),
    quantity: new FormControl<number>(1)
  });

  subscriptionTypesControl = new FormControl<number[]>([]);

  campaignId: number | null = null;
  isFinishedCampaign = false;
  gifts: Array<CampaignGift & { productVariantName?: string }> = [];
  subscriptionTypes: SubscriptionType[] = [];
  validatingEan = false;
  subscriptionTypesTree: SelectItemGroup[] = [];
  coverUrl = '';

  readonly statusOptions = [
    { label: 'Rascunho', value: 'DRAFT' },
    { label: 'Ativa', value: 'ACTIVE' },
    { label: 'Finalizada', value: 'FINISHED', disabled: true },
    { label: 'Cancelada', value: 'CANCELLED' }
  ];

  readonly ruleTypeOptions = [{ label: 'Indicação (MGM)', value: 'MGM' }];

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Configurar Regras');
    await this.loadSubscriptionTypes();
    const id = this.route.snapshot.paramMap.get('campaignId');
    if (id) {
      this.campaignId = Number(id);
      await this.loadCampaign();
    }
  }

  async loadCampaign(): Promise<void> {
    LoaderService.showLoader();
    try {
      const campaign = await lastValueFrom(
        this.campaignService
          .getCampaign(this.campaignId)
          .pipe(map((r) => r.result))
      );
      this.form.patchValue({
        ...campaign,
        startDate: this.parseLocalDate(campaign?.startDate),
        endDate: this.parseLocalDate(campaign?.endDate)
      });
      this.coverUrl = this.form.get('coverUrl')?.value || '';
      this.applyCampaignEditMode(campaign);
      await Promise.all([
        this.loadGifts(),
        this.loadSubscriptionTypeRestrictions()
      ]);
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async loadGifts(): Promise<void> {
    if (!this.campaignId) return;
    const gifts = await lastValueFrom(
      this.campaignService.listGifts(this.campaignId).pipe(map((r) => r.result))
    );
    this.gifts = await Promise.all(
      gifts.map(async (gift) => {
        if (!gift.internalEan) return gift;
        try {
          const validation = await lastValueFrom(
            this.campaignService
              .validateProduct({ ean: gift.internalEan })
              .pipe(map((r) => r.result))
          );
          return {
            ...gift,
            productVariantName: validation?.productVariantName,
            productVariantId: validation?.productVariantId || gift.productVariantId,
            costPrice: validation?.costPrice ?? gift.costPrice,
            invoicePrice: validation?.invoicePrice ?? gift.invoicePrice,
            weight: validation?.weight ?? gift.weight
          };
        } catch {
          return gift;
        }
      })
    );
  }

  async loadSubscriptionTypes(): Promise<void> {
    const excludedIds = [SubscriptionEnum.Prime, SubscriptionEnum.Select];
    LoaderService.showLoader();
    try {
      this.subscriptionTypes = (
        await Promise.all(
          getAllSubscriptions()
            .filter((s) => !excludedIds.includes(s.value))
            .map((s) =>
              lastValueFrom(
                this.subscriptionService
                  .findSubscriptionTypeList(s.value)
                  .pipe(map((data) => data.result))
              )
            )
        )
      ).reduce(
        (list: SubscriptionType[], sTypes) => (list = list.concat(sTypes)),
        []
      );
      this.configureTree();
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async loadSubscriptionTypeRestrictions(): Promise<void> {
    if (!this.campaignId) return;
    const restrictions = await lastValueFrom(
      this.campaignService
        .listSubscriptionTypes(this.campaignId)
        .pipe(map((r) => r.result))
    );
    this.subscriptionTypesControl.setValue(
      restrictions
        .map((r) => r.subscriptionTypeId)
        .filter((id) => id !== undefined) as number[]
    );
  }

  async addGift(): Promise<void> {
    if (this.isFinishedCampaign) {
      this.showReadOnlyMessage();
      return;
    }
    const ean = this.giftForm.controls['ean'].value?.trim();
    const quantity = this.giftForm.controls['quantity'].value || 1;
    if (!ean) return;

    if (this.gifts.some((g) => g.internalEan === ean)) {
      this.messageService.add({
        severity: 'error',
        summary: 'EAN duplicado',
        detail: 'Este brinde já foi adicionado à campanha.'
      });
      return;
    }

    this.validatingEan = true;
    try {
      const validation = await lastValueFrom(
        this.campaignService
          .validateProduct({ ean })
          .pipe(map((r) => r.result))
      );
      if (!validation?.productVariantId) {
        this.messageService.add({
          severity: 'error',
          summary: 'EAN inválido',
          detail: 'Nenhum produto encontrado com o EAN informado.'
        });
        return;
      }
      this.gifts.push({
        internalEan: ean,
        quantity,
        productVariantName: validation.productVariantName,
        productVariantId: validation.productVariantId,
        costPrice: validation.costPrice,
        invoicePrice: validation.invoicePrice,
        weight: validation.weight
      });
      this.giftForm.reset({ ean: '', quantity: 1 });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.validatingEan = false;
    }
  }

  removeGift(index: number): void {
    if (this.isFinishedCampaign) {
      this.showReadOnlyMessage();
      return;
    }
    this.gifts.splice(index, 1);
  }

  async saveCampaign(): Promise<void> {
    if (this.isFinishedCampaign) {
      this.showReadOnlyMessage();
      return;
    }
    if (this.form.invalid) return;
    LoaderService.showLoader();
    try {
      const payload: Partial<Campaign> = {
        ...this.form.value,
        startDate: this.formatDate(this.form.value.startDate),
        endDate: this.formatDate(this.form.value.endDate)
      };
      if (!this.campaignId) {
        const created = await lastValueFrom(
          this.campaignService
            .createCampaign(payload)
            .pipe(map((r) => r.result))
        );
        this.campaignId = created.campaignId;
        await this.router.navigate(['/marketing/campaigns', this.campaignId]);
      } else {
        await lastValueFrom(
          this.campaignService
            .updateCampaign(payload, this.campaignId)
            .pipe(map((r) => r.result))
        );
      }
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async saveRules(): Promise<void> {
    if (this.isFinishedCampaign) {
      this.showReadOnlyMessage();
      return;
    }
    await this.saveCampaign();
    if (!this.campaignId) return;
    LoaderService.showLoader();
    try {
      await Promise.all([
        lastValueFrom(
          this.campaignService.replaceGifts(
            {
              gifts: this.gifts.map((g) => ({
                ean: g.internalEan,
                quantity: g.quantity || 1
              }))
            },
            this.campaignId
          )
        ),
        lastValueFrom(
          this.campaignService.replaceSubscriptionTypes(
            { subscriptionTypeIds: this.subscriptionTypesControl.value || [] },
            this.campaignId
          )
        )
      ]);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Regras salvas com sucesso.'
      });
      await this.router.navigate(['/marketing/campaigns']);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async onFileSelect(event: FileUploadHandlerEvent): Promise<void> {
    if (this.isFinishedCampaign) {
      this.showReadOnlyMessage();
      return;
    }
    const file = event.files[0];
    if (!file) return;
    LoaderService.showLoader();
    try {
      const filePath = '/marketing/campaigns/covers/';
      const fileName = `${new Date().getTime()}_${file.name}`;
      const uploadedUrl = await lastValueFrom(
        this.fileService
          .uploadFileForm(filePath, file, fileName)
          .pipe(map((r) => r.result))
      );
      this.form.patchValue({ coverUrl: uploadedUrl });
      this.coverUrl = uploadedUrl;
      this.fileUpload.clear();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  subscriptionLabel(subscriptionTypeId: number): string {
    const st = this.subscriptionTypes.find(
      (s) => s.subscriptionTypeId === subscriptionTypeId
    );
    if (!st) return '';
    return `${getSubscriptionName(st.subscriptionId)} ${st.name}`;
  }

  invalidControl(control: string): boolean {
    return (
      this.form.controls[control].invalid &&
      (this.form.controls[control].dirty || this.form.controls[control].touched)
    );
  }

  private parseLocalDate(value?: string | Date | null): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parts = value.split('-').map((p) => Number(p));
    if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return null;
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  private formatDate(value?: Date | string | null): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return this.datePipe.transform(value, 'yyyy-MM-dd');
  }

  private configureTree(): void {
    if (this.subscriptionTypes?.length) {
      this.subscriptionTypesTree = this.subscriptionTypes.reduce(
        (list: SelectItemGroup[], st) => {
          const exists = list.find((n) => n.value === st.subscriptionId);
          if (!exists) {
            list.push({
              value: st.subscriptionId,
              label: getSubscriptionName(st.subscriptionId),
              items: [
                {
                  value: st.subscriptionTypeId,
                  label: st.name,
                  title: `${getSubscriptionName(st.subscriptionId)} ${st.name}`
                }
              ]
            });
          } else {
            exists.items.push({
              value: st.subscriptionTypeId,
              label: st.name,
              title: `${getSubscriptionName(st.subscriptionId)} ${st.name}`
            });
          }
          return list;
        },
        []
      );
    }
  }

  private applyCampaignEditMode(campaign: Campaign): void {
    const status = (campaign?.status || '').toUpperCase();
    this.isFinishedCampaign = status === 'FINISHED';
    if (this.isFinishedCampaign) {
      this.form.disable({ emitEvent: false });
      this.giftForm.disable({ emitEvent: false });
      this.subscriptionTypesControl.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private showReadOnlyMessage(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Campanha finalizada',
      detail: 'Campanhas finalizadas não podem ser editadas.'
    });
  }
}
